import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CoverNoteModal } from '../../components/coverNote/CoverNoteModal';
import { UpdateStoredLocationModal } from '../../components/recordRoom/UpdateStoredLocationModal';
import { DataTable, type Column } from '../../components/table/DataTable';
import { EMPTY_CELL, hasStorageLocation, type CompletedFileRow } from '../../data/recordRoom';
import {
  fetchPhysicalFileColumnSuggestions,
  listPhysicalFiles,
  SORT_COL_MAP,
  updateStoredLocation,
} from '../../services/recordRoom';

function isEmptyCell(value: string): boolean {
  return !value.trim() || value === EMPTY_CELL;
}

export default function CompletedFilesPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CompletedFileRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverRow, setCoverRow] = useState<CompletedFileRow | null>(null);
  const [editRow, setEditRow] = useState<CompletedFileRow | null>(null);
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'completedAt', dir: 'asc' });

  const fetchAbortRef = useRef<AbortController | null>(null);
  const filtersRef = useRef<Record<string, string>>({});

  const fetchList = useCallback(
    (p: number, size: number, filters: Record<string, string>, sortKey: string, sortDir: 'asc' | 'desc') => {
      fetchAbortRef.current?.abort();
      fetchAbortRef.current = new AbortController();
      const { signal } = fetchAbortRef.current;
      setLoading(true);
      setError(null);

      void listPhysicalFiles({
        page: p - 1,
        pageSize: size,
        columnFilters: filters,
        sortBy: SORT_COL_MAP[sortKey] ?? 'REFERENCE_NO',
        sortDir: sortDir === 'asc' ? 'ASC' : 'DESC',
        signal,
      })
        .then((result) => {
          if (signal.aborted) return;
          setRows(result.items);
          setTotalCount(result.total);
          setTotalPages(result.totalPages);
        })
        .catch(() => {
          if (signal.aborted) return;
          setError(t('recordRoom.loadFailed'));
          setRows([]);
          setTotalCount(0);
          setTotalPages(1);
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [t],
  );

  const fetchRef = useRef(fetchList);
  fetchRef.current = fetchList;

  useEffect(() => {
    fetchRef.current(page, pageSize, filtersRef.current, sort.key, sort.dir);
    return () => fetchAbortRef.current?.abort();
  }, [page, pageSize, sort.key, sort.dir]);

  const handleFiltersChange = useCallback(
    (colFilters: Record<string, string>) => {
      filtersRef.current = colFilters;
      setPage(1);
      fetchRef.current(1, pageSize, colFilters, sort.key, sort.dir);
    },
    [pageSize, sort.key, sort.dir],
  );

  const handleSortChange = useCallback((key: string, dir: 'asc' | 'desc') => {
    setSort({ key, dir });
    setPage(1);
  }, []);

  const handleUpdateLocation = useCallback(
    async (payload: { caseRequestId: string; fileRef: string; location: string }) => {
      setSavingLocation(true);
      setLocationError(null);
      const result = await updateStoredLocation(payload);
      setSavingLocation(false);

      if (!result.success) {
        setLocationError(result.message);
        return;
      }

      setEditRow(null);
      fetchRef.current(page, pageSize, filtersRef.current, sort.key, sort.dir);
    },
    [page, pageSize, sort.key, sort.dir],
  );

  const columns = useMemo<Column<CompletedFileRow>[]>(() => {
    return [
      {
        key: 'ref',
        header: t('tasks.shared.fileRefNo'),
        getValue: (r) => r.ref,
        filterPattern: /^[a-zA-Z0-9/._ -]*$/,
        render: (r) => <span className="rn">{r.ref}</span>,
      },
      { key: 'fileCategory', header: t('tasks.shared.fileCategory'), getValue: (r) => r.fileCategory },
      {
        key: 'subFileCategory',
        header: t('tasks.shared.subFileCategory'),
        getValue: (r) => r.subFileCategory,
        render: (r) => (
          <span style={{ color: isEmptyCell(r.subFileCategory) ? 'var(--t3)' : undefined }}>{r.subFileCategory}</span>
        ),
      },
      { key: 'agency', header: t('tasks.shared.submittedAgency'), getValue: (r) => r.agency },
      {
        key: 'completedAt',
        header: t('recordRoom.caseCompletedAt'),
        getValue: (r) => r.completedAt,
        filterType: 'date-range',
        render: (r) => (
          <span style={{ fontSize: 12, color: isEmptyCell(r.completedAt) ? 'var(--t3)' : undefined }}>{r.completedAt}</span>
        ),
      },
      {
        key: 'fs',
        header: t('recordRoom.physicalFileStatus'),
        getValue: (r) => r.statusLabel,
        render: (r) => <span className={`pill ${r.statusClass}`}>{r.statusLabel}</span>,
      },
      {
        key: 'movementAt',
        header: t('recordRoom.fileMovementDateTime'),
        getValue: (r) => r.movementAt,
        filterType: 'date-range',
        render: (r) => (
          <span style={{ fontSize: 12, color: isEmptyCell(r.movementAt) ? 'var(--t3)' : undefined }}>{r.movementAt}</span>
        ),
      },
      {
        key: 'custody',
        header: t('recordRoom.physicalFileCustody'),
        getValue: (r) => r.custody,
        render: (r) => (
          <span style={{ color: isEmptyCell(r.custody) ? 'var(--t3)' : 'var(--t2)' }}>{r.custody}</span>
        ),
      },
      {
        key: 'storageLocation',
        header: t('recordRoom.storageLocation'),
        getValue: (r) => r.storageLocation,
        render: (r) => (
          <span style={{ fontSize: 12, color: isEmptyCell(r.storageLocation) ? 'var(--t3)' : undefined }}>
            {r.storageLocation}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => {
          const canViewCover = Boolean(r.caseRequestId?.trim());
          const canUpdateLoc = hasStorageLocation(r.storageLocation);
          return (
            <div className="act-wrap">
              <button
                className="act-b"
                type="button"
                data-tip={canViewCover ? t('recordRoom.viewCoverNote') : t('recordRoom.coverNoteUnavailable')}
                disabled={!canViewCover}
                onClick={() => canViewCover && setCoverRow(r)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
              {canUpdateLoc ? (
                <button
                  className="act-b"
                  type="button"
                  data-tip={t('recordRoom.updateStorageLocation')}
                  onClick={() => {
                    setLocationError(null);
                    setEditRow(r);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </button>
              ) : null}
            </div>
          );
        },
      },
    ];
  }, [t]);

  return (
    <>
      <div className="bc">
        <span>{t('recordRoom.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('recordRoom.title')}</div>
          {/* <div className="pg-sub">View a list of all completed cases and archived physical case files.</div> */}
        </div>
      </div>

      {error ? <div className="si-err" style={{ marginBottom: 12 }}>{error}</div> : null}

      <DataTable
        title={t('recordRoom.tableTitle')}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.caseRequestId || r.ref}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 20]}
        previousLabel={t('tasks.shared.previousBack')}
        nextLabel={t('tasks.shared.nextForward')}
        loading={loading}
        fetchColumnSuggestions={fetchPhysicalFileColumnSuggestions}
        onFiltersChange={handleFiltersChange}
        serverSort={{
          key: sort.key,
          dir: sort.dir,
          onSortChange: handleSortChange,
        }}
        serverPaging={{
          totalCount,
          totalPages,
          page,
          pageSize,
          onPageChange: (p) => setPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setPage(1);
          },
        }}
      />

      <CoverNoteModal
        open={!!coverRow}
        onClose={() => setCoverRow(null)}
        caseRequestId={coverRow?.caseRequestId}
        fileRefFallback={coverRow?.ref}
      />

      <UpdateStoredLocationModal
        open={!!editRow}
        fileRef={editRow?.ref ?? ''}
        initialLocation={editRow && !isEmptyCell(editRow.storageLocation) ? editRow.storageLocation : ''}
        onClose={() => {
          setEditRow(null);
          setLocationError(null);
        }}
        onUpdate={(payload) =>
          handleUpdateLocation({
            caseRequestId: editRow?.caseRequestId ?? '',
            fileRef: payload.fileRef,
            location: payload.location,
          })
        }
        saving={savingLocation}
        errorMessage={locationError}
      />
    </>
  );
}
